import { useMemo } from 'react';

import { useOddsGridContext } from '../context/OddsGridContext';
import { useSelector } from '../providers/StoreProvider';

export const useBookmakersAll = () => {
  const bookmakers = useSelector(state => state.bookmakers);
  const { overrideBookies } = useOddsGridContext();

  // Every bookmaker returned by the API is shown. Branding is resolved from each bookmaker
  // object via useBookmakerData.
  //
  // Ordering (order-only override semantics): when `overrideBookies` is present, listed
  // bookmakers are moved to the front in ascending override-priority order; unlisted bookmakers
  // follow in their existing API-priority order (lowest first). `originalIndex` keeps the sort
  // stable within each tier. Without an override this reduces to the plain API-priority sort.
  return useMemo(() => {
    const overridePriorityByCode = new Map((overrideBookies ?? []).map(bookie => [bookie.code, bookie.priority]));

    return [...(bookmakers || [])]
      .map((bookmaker, originalIndex) => ({ bookmaker, originalIndex }))
      .sort((left, right) => {
        const leftOverride = overridePriorityByCode.get(left.bookmaker.code);
        const rightOverride = overridePriorityByCode.get(right.bookmaker.code);

        // Both overridden → order by override priority; overridden always precedes non-overridden.
        if (leftOverride !== undefined && rightOverride !== undefined) {
          return leftOverride - rightOverride || left.originalIndex - right.originalIndex;
        }
        if (leftOverride !== undefined) return -1;
        if (rightOverride !== undefined) return 1;

        return left.bookmaker.priority - right.bookmaker.priority || left.originalIndex - right.originalIndex;
      })
      .map(entry => entry.bookmaker);
  }, [bookmakers, overrideBookies]);
};
